import * as React from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'

import { Box, Button, Section, useToggle } from '@factorialco/deprecated-design-system'
import blankSlate from 'assets/images/list_blankslate.svg'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import Popover from 'modules/core/components/Popover'
import type { Option } from 'modules/core/components/Popover/Content'
import PopoverContent from 'modules/core/components/Popover/Content'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'
import users, { employeeUser } from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import collectionEmployees from 'modules/employees/store/collections/employees'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { PermissionGroupsLayout } from 'modules/permissions/components/PermissionGroupsLayout'
import AssignUsersModal from 'modules/permissions/components/Permissions/AssignUsersModal'
import {
  PERMISSION_GROUP_KIND_ADMIN,
  PERMISSION_GROUP_KIND_DEFAULT,
} from 'modules/permissions/lib/constants'

import { PermissionGroupContainerProps } from '../PermissionGroupContainer'
import Item from './Item'

const Employees = observer(() => {
  const { permissionGroup } = useOutletContext<PermissionGroupContainerProps>()

  const [isEmployeesModalOpen, setIsEmployeesModalOpen] = React.useState(false)
  const [open, toggle] = useToggle(false)

  const employees = permissionGroup.employees

  const assignableUsers = (): User[] => {
    return collectionEmployees
      .filter((e) => !e.isTerminated)
      .map((employee) => employeeUser(employee))
  }

  const description = (): string => {
    if (
      permissionGroup.attributes.kind === PERMISSION_GROUP_KIND_ADMIN ||
      permissionGroup.attributes.legacy_admin
    ) {
      return i18n.t('settings.permissions.group.employees_section.legacy_admin.description')
    }

    if (
      permissionGroup.attributes.kind === PERMISSION_GROUP_KIND_DEFAULT ||
      permissionGroup.attributes.default_group
    ) {
      return i18n.t('settings.permissions.group.employees_section.default_group.description')
    }

    return i18n.t('settings.permissions.group.employees_section.other_group.description')
  }

  const options = (): Option[] => {
    if (!users.current.policy.can('permissions.groupAssignEmployees')) return []

    return [
      {
        onClick: () => setIsEmployeesModalOpen(true),
        title: i18n.t('settings.permissions.group.actions.assign_employees.title'),
        description: i18n.t('settings.permissions.group.actions.assign_employees.description'),
      },
    ]
  }

  const { loading } = useAsyncData(
    () => contractVersions.fetch({ withCachedFetchParams: true }),
    []
  )

  return (
    <ResponsivePage.Body showHeader={false} loading={loading} error={null}>
      <PermissionGroupsLayout />
      <Section.FullWidth
        illustration="group"
        title={permissionGroup.title}
        description={description()}
      >
        {options().length && (
          <Box alignItems="flexEnd" paddingBottom="s24">
            <Popover
              attachment="top right"
              targetAttachment="bottom center"
              toggle={toggle}
              open={open}
            >
              <Button onClick={toggle(true)} iconName="add" />
              <PopoverContent onClose={toggle(false)} options={options()} />
            </Popover>
          </Box>
        )}
        {isEmployeesModalOpen && (
          <AssignUsersModal
            onClose={() => setIsEmployeesModalOpen(false)}
            permissionGroup={permissionGroup}
            users={assignableUsers()}
            contractVersions={contractVersions}
          />
        )}
        <>
          {employees.length === 0 ? (
            <img src={blankSlate} height={300} />
          ) : (
            <BoxList>
              {employees.map((employee) => (
                <Item
                  key={employee.attributes.id}
                  permissionGroup={permissionGroup}
                  employee={employee}
                  contractVersions={contractVersions}
                />
              ))}
            </BoxList>
          )}
          <Outlet />
        </>
      </Section.FullWidth>
    </ResponsivePage.Body>
  )
})

export default Employees
